export type AppContext = "super-admin";

export const APP_CONTEXTS: readonly AppContext[] = ["super-admin"] as const;

export function buildAppRoutePath(
  context: AppContext,
  module: string,
  ...submodules: readonly string[]
) {
  return `/${[context, module, ...submodules].join("/")}`;
}

export type RouteIcon =
  | "assets"
  | "audit"
  | "categories"
  | "dashboard"
  | "forms"
  | "inventory"
  | "jobs"
  | "library"
  | "packs"
  | "plans"
  | "settings"
  | "team"
  | "templates"
  | "tenants"
  | "verticals";

export const ROUTE_PATHS = {
  root: "/",
  authSignIn: "/sign-in",
  superAdminDashboard: buildAppRoutePath("super-admin", "dashboard"),
  superAdminTenants: buildAppRoutePath("super-admin", "tenants"),
  superAdminPlans: buildAppRoutePath("super-admin", "plans"),
  superAdminLibrary: buildAppRoutePath("super-admin", "library"),
  superAdminVerticals: buildAppRoutePath("super-admin", "library", "verticals"),
  superAdminCategories: buildAppRoutePath(
    "super-admin",
    "library",
    "categories",
  ),
  superAdminReviewQueue: buildAppRoutePath(
    "super-admin",
    "library",
    "review-queue",
  ),
  superAdminTemplates: buildAppRoutePath("super-admin", "library", "templates"),
  superAdminPacks: buildAppRoutePath(
    "super-admin",
    "library",
    "template-packs",
  ),
  superAdminAccessPresets: buildAppRoutePath("super-admin", "access-presets"),
  superAdminAuditLog: buildAppRoutePath("super-admin", "audit-log"),
} as const;

export type RouteKey = keyof typeof ROUTE_PATHS;
export type SidebarBrandTone = "orange" | "violet";

export type AppRouteConfig = {
  readonly key: RouteKey;
  readonly label: string;
  readonly path: string;
  readonly context: AppContext;
  readonly icon: RouteIcon;
  readonly children?: readonly RouteKey[];
  readonly placeholder?: boolean;
  readonly redirectTo?: RouteKey;
};

export type AppRoutesTree = {
  readonly auth: {
    readonly signIn: AppRouteConfig;
  };
  readonly root: AppRouteConfig;
  readonly superAdmin: {
    readonly auditLog: AppRouteConfig;
    readonly dashboard: AppRouteConfig;
    readonly plans: AppRouteConfig;
    readonly library: {
      readonly categories: AppRouteConfig;
      readonly index: AppRouteConfig;
      readonly packs: AppRouteConfig;
      readonly review: AppRouteConfig;
      readonly templates: AppRouteConfig;
      readonly verticals: AppRouteConfig;
    };
    readonly accessPresets: AppRouteConfig;
    readonly tenants: AppRouteConfig;
  };
};

export const APP_ROUTES = {
  auth: {
    signIn: {
      key: "authSignIn",
      label: "Sign In",
      path: ROUTE_PATHS.authSignIn,
      context: "super-admin",
      icon: "settings",
    },
  },
  root: {
    key: "root",
    label: "Home",
    path: ROUTE_PATHS.root,
    context: "super-admin",
    icon: "dashboard",
  },
  superAdmin: {
    dashboard: {
      key: "superAdminDashboard",
      label: "Dashboard",
      path: ROUTE_PATHS.superAdminDashboard,
      context: "super-admin",
      icon: "dashboard",
    },
    tenants: {
      key: "superAdminTenants",
      label: "Tenants",
      path: ROUTE_PATHS.superAdminTenants,
      context: "super-admin",
      icon: "tenants",
    },
    plans: {
      key: "superAdminPlans",
      label: "Plans",
      path: ROUTE_PATHS.superAdminPlans,
      context: "super-admin",
      icon: "plans",
    },
    library: {
      index: {
        key: "superAdminLibrary",
        label: "Library",
        path: ROUTE_PATHS.superAdminLibrary,
        context: "super-admin",
        icon: "library",
        children: [
          "superAdminVerticals",
          "superAdminCategories",
          "superAdminReviewQueue",
          "superAdminTemplates",
          "superAdminPacks",
        ],
        redirectTo: "superAdminVerticals",
      },
      verticals: {
        key: "superAdminVerticals",
        label: "Verticals",
        path: ROUTE_PATHS.superAdminVerticals,
        context: "super-admin",
        icon: "verticals",
      },
      categories: {
        key: "superAdminCategories",
        label: "Categories",
        path: ROUTE_PATHS.superAdminCategories,
        context: "super-admin",
        icon: "categories",
      },
      review: {
        key: "superAdminReviewQueue",
        label: "Review Queue",
        path: ROUTE_PATHS.superAdminReviewQueue,
        context: "super-admin",
        icon: "templates",
      },
      templates: {
        key: "superAdminTemplates",
        label: "Templates",
        path: ROUTE_PATHS.superAdminTemplates,
        context: "super-admin",
        icon: "templates",
      },
      packs: {
        key: "superAdminPacks",
        label: "Template Packs",
        path: ROUTE_PATHS.superAdminPacks,
        context: "super-admin",
        icon: "packs",
      },
    },
    accessPresets: {
      key: "superAdminAccessPresets",
      label: "Access Presets",
      path: ROUTE_PATHS.superAdminAccessPresets,
      context: "super-admin",
      icon: "settings",
    },
    auditLog: {
      key: "superAdminAuditLog",
      label: "Audit log",
      path: ROUTE_PATHS.superAdminAuditLog,
      context: "super-admin",
      icon: "audit",
    },
  },
} as const satisfies AppRoutesTree;

export const SIDEBAR_NAVIGATION: Record<
  AppContext,
  {
    brandTone: SidebarBrandTone;
    items: readonly RouteKey[];
    tenantLabel: string;
  }
> = {
  "super-admin": {
    tenantLabel: "Super Admin - Brightcomms",
    brandTone: "violet",
    items: [
      "superAdminDashboard",
      "superAdminLibrary",
      "superAdminTenants",
      "superAdminPlans",
      "superAdminAccessPresets",
      "superAdminAuditLog",
    ],
  },
} as const;

export const CONTEXT_SWITCHER_ROUTES: readonly {
  readonly context: AppContext;
  readonly label: string;
  readonly path: (typeof ROUTE_PATHS)[RouteKey];
}[] = [
  {
    context: "super-admin",
    label: "Super Admin (Brightcomms)",
    path: APP_ROUTES.superAdmin.library.verticals.path,
  },
] as const;
